/**
 * @fileoverview Hook for fetching all livestreams for a game
 *
 * Provides a React hook to fetch and manage the list of all livestreams
 * (league, home, visitor) configured for a specific game.
 *
 * @module hooks/useLivestreams
 */

import { useCallback, useEffect, useState } from 'react'
import req from '../api/request'
import type {
  Livestream,
  LivestreamListResponse,
  UseLivestreamsResult,
} from '../types/Livestream'

/**
 * Hook to fetch all livestreams for a game
 *
 * Retrieves all configured livestreams (league, home, visitor) for a specific game.
 * Follows the established hook pattern with loading, error, and refetch capabilities.
 *
 * @param gameId - The ID of the game to fetch livestreams for
 * @returns Object containing livestreams array, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * function GameLivestreams({ gameId }: { gameId: string }) {
 *   const { livestreams, loading, error, refetch } = useLivestreams(gameId)
 *
 *   if (loading) return <Spinner />
 *   if (error) return <ErrorState message={error.message} onRetry={refetch} />
 *
 *   return (
 *     <div>
 *       {livestreams.map(stream => (
 *         <StreamCard
 *           key={stream.id}
 *           type={stream.type}
 *           status={stream.status}
 *           streamUrl={stream.stream_url}
 *         />
 *       ))}
 *     </div>
 *   )
 * }
 * ```
 */
export const useLivestreams = (gameId?: string): UseLivestreamsResult => {
  const [livestreams, setLivestreams] = useState<Livestream[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Fetches all livestreams for the given game
   */
  const fetchLivestreams = useCallback(async (): Promise<void> => {
    if (!gameId) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = (await req(
        `/games/${gameId}/livestreams`
      )) as LivestreamListResponse

      setLivestreams(response.data)
    } catch (err) {
      const fetchError =
        err instanceof Error ? err : new Error('Failed to fetch livestreams')
      setError(fetchError)
    } finally {
      setLoading(false)
    }
  }, [gameId])

  // Auto-fetch on mount and gameId change
  useEffect(() => {
    fetchLivestreams()
  }, [fetchLivestreams])

  return {
    livestreams,
    loading,
    error,
    refetch: fetchLivestreams,
  }
}

export default useLivestreams
